import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Reveal } from '../components/Reveal';
import { BrushDivider, EnsoAnimated, FujiLines, BonsaiMark } from '../components/Motifs';

const FUJI_MIST = '/images/fuji-mist.jpg';

const pillars = [
    {
        num: '01',
        slug: 'partnerships',
        jp: '提携',
        title: 'Partnerships',
        heading: 'Building bridges across markets and industries.',
        body: 'We help scale-ups and established players forge partnerships that accelerate growth — particularly across Asia\'s industrial innovation ecosystem. From identifying the right partners to structuring collaboration frameworks, we guide your expansion with both global perspective and local understanding.',
        items: [
            'Strategic partnership design and negotiations',
            'Asia market entry and business development support',
            'Industry collaboration, joint ventures, and ecosystem building',
            'Connecting innovators, investors, and operators in energy, infrastructure, and clean technology',
        ],
        outcome: 'Enduring partnerships that extend reach, capability, and market presence.',
    },
    {
        num: '02',
        slug: 'transactions',
        jp: '取引',
        title: 'Transactions',
        heading: 'Supporting asset sales, acquisitions, and strategic initiatives.',
        body: 'Whether you are evaluating a sale, investment, or partnership, we provide the insight and structure to ensure the best outcome. Our transaction support combines deep sector understanding with a disciplined, project-based approach.',
        items: [
            'Deal strategy, preparation, and execution support',
            'Buy-side and sell-side advisory for private assets and project portfolios',
            'Independent project-based consultancy for infrastructure and energy assets',
        ],
        outcome: 'Stronger decisions, smoother execution, and greater value realization from every transaction.',
    },
    {
        num: '03',
        slug: 'equity-story',
        jp: '物語',
        title: 'Equity Story',
        heading: 'Crafting clarity and conviction for investors and stakeholders.',
        body: 'Your equity story is more than a presentation — it\'s the narrative that defines your value, ambition, and direction. We help companies articulate their strategy and potential in ways that resonate with investors, partners, and markets.',
        items: [
            'Shaping compelling growth narratives for investors and boards',
            'Refining business models, metrics, and performance communication',
            'Preparing for funding rounds, strategic partnerships, strategic events',
            'Evaluating and advising on positioning against peers',
        ],
        outcome: 'A sharper, more credible market positioning that inspires confidence and unlocks capital.',
    },
];

const Services = () => {
    useEffect(() => {
        document.title = 'Services — Nakasendo Capital';
    }, []);

    return (
        <div data-testid="services-page">
            {/* HERO */}
            <section className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-16 pb-20 lg:pb-32 overflow-hidden">
                <div
                    aria-hidden="true"
                    style={{ color: 'rgba(48, 58, 82, 0.05)' }}
                    className="absolute -left-6 top-10 font-jp text-[24vw] md:text-[14vw] leading-none pointer-events-none select-none whitespace-nowrap font-light"
                >
                    専門
                </div>
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-9">
                        <Reveal>
                            <p className="overline">— Our Expertise</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h1 className="mt-8 h1-editorial text-[clamp(2.5rem,7vw,7rem)] max-w-[16ch]">
                                Guiding companies on their journey to <em>lasting value.</em>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.4}>
                            <BrushDivider className="w-32 h-3 mt-10" color="#1A1A1A" />
                        </Reveal>
                        <Reveal delay={0.55}>
                            <p className="mt-8 max-w-2xl text-ink-soft text-lg md:text-xl font-light leading-relaxed">
                                We work alongside leaders to help their businesses grow, transform, and
                                realize their full potential. Our expertise spans three complementary
                                areas — designed to support you through each stage of your value
                                creation journey.
                            </p>
                        </Reveal>
                    </div>
                </div>

                {/* Pillar nav */}
                <div className="mt-16 lg:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border-y border-ink/10">
                    {pillars.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.1} className="bg-ivory">
                            <a
                                href={`#${p.slug}`}
                                data-testid={`services-jump-${p.slug}`}
                                className="group flex items-baseline justify-between gap-4 p-6 md:p-8 transition-colors duration-500 hover:bg-aizome hover:text-ivory"
                            >
                                <div className="flex items-baseline gap-6">
                                    <span className="numeral text-3xl md:text-4xl group-hover:text-ivory/60 transition-colors duration-500">
                                        {p.num}
                                    </span>
                                    <span className="font-serif text-2xl md:text-3xl tracking-tight">
                                        {p.title}
                                    </span>
                                </div>
                                <span className="overline group-hover:text-ivory transition-colors duration-500">↓</span>
                            </a>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PILLAR 01 — PARTNERSHIPS */}
            <PillarSection
                pillar={pillars[0]}
                visual={
                    <div className="relative aspect-[4/5] bg-aizome overflow-hidden">
                        <div
                            aria-hidden="true"
                            style={{ color: 'rgba(253, 251, 247, 0.10)' }}
                            className="absolute inset-0 font-jp text-[28vw] leading-none flex items-center justify-center font-light"
                        >
                            提携
                        </div>
                        <EnsoAnimated
                            className="absolute top-12 left-12 w-32 h-32 text-ivory opacity-60"
                            stroke="#FDFBF7"
                            size={140}
                            delay={0.2}
                        />
                        <div className="absolute bottom-8 left-8 right-8 text-ivory">
                            <p className="overline text-ivory/60">— 01 / Partnerships</p>
                            <p className="font-serif text-3xl mt-3 tracking-tight">
                                Bridges that <em className="italic font-light">last.</em>
                            </p>
                        </div>
                    </div>
                }
                imageLeft={false}
            />

            {/* PILLAR 02 — TRANSACTIONS */}
            <PillarSection
                pillar={pillars[1]}
                visual={
                    <div className="relative aspect-[4/5] overflow-hidden img-frame border border-ink/10">
                        <img
                            src={FUJI_MIST}
                            alt="Misty mountain symbolizing transactions and scale"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-aizome/90 via-aizome/20 to-transparent" />
                        <div className="absolute bottom-8 left-8 right-8 text-ivory">
                            <p className="overline text-ivory/60">— 02 / Transactions</p>
                            <p className="font-serif text-3xl mt-3 tracking-tight">
                                Disciplined <em className="italic font-light">execution.</em>
                            </p>
                        </div>
                    </div>
                }
                imageLeft={true}
            />

            {/* PILLAR 03 — EQUITY STORY */}
            <PillarSection
                pillar={pillars[2]}
                visual={
                    <div className="relative aspect-[4/5] bg-ivory border border-ink/15 overflow-hidden">
                        <FujiLines
                            className="absolute bottom-0 left-0 right-0 w-full h-2/3 text-aizome opacity-60"
                            stroke="#303a52"
                        />
                        <div
                            aria-hidden="true"
                            style={{ color: 'rgba(48, 58, 82, 0.08)' }}
                            className="absolute inset-0 font-jp text-[28vw] leading-none flex items-center justify-center font-light"
                        >
                            物語
                        </div>
                        <BonsaiMark
                            className="absolute top-10 left-10 text-aizome opacity-90"
                            stroke="#303a52"
                            size={70}
                        />
                        <div className="absolute top-8 right-8 text-right">
                            <p className="overline">— 03 / Equity Story</p>
                            <p className="font-serif text-3xl mt-3 tracking-tight text-ink">
                                Narratives of <em className="italic font-light">conviction.</em>
                            </p>
                        </div>
                    </div>
                }
                imageLeft={false}
            />

            {/* CLOSING CTA */}
            <section className="relative px-6 md:px-12 lg:px-24 py-24 lg:py-40 border-t border-ink/10 text-center overflow-hidden">
                <Reveal>
                    <p className="overline">— Every Step Counts</p>
                </Reveal>
                <Reveal delay={0.15}>
                    <h2 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.0] max-w-4xl mx-auto">
                        Where would you like to <em className="italic font-light">begin?</em>
                    </h2>
                </Reveal>
                <Reveal delay={0.4}>
                    <Link to="/contact" data-testid="services-cta" className="btn-primary mt-12">
                        Contact Us <span className="arrow">→</span>
                    </Link>
                </Reveal>
            </section>
        </div>
    );
};

const PillarSection = ({ pillar, visual, imageLeft }) => {
    return (
        <section
            id={pillar.slug}
            className="px-6 md:px-12 lg:px-24 py-20 lg:py-32 border-t border-ink/10 scroll-mt-24"
            data-testid={`pillar-${pillar.slug}`}
        >
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${imageLeft ? '' : ''}`}>
                {/* Visual */}
                <div className={`lg:col-span-5 ${imageLeft ? 'lg:order-1' : 'lg:order-2'}`}>
                    <Reveal y={40}>{visual}</Reveal>
                </div>

                {/* Text */}
                <div className={`lg:col-span-7 ${imageLeft ? 'lg:order-2' : 'lg:order-1'}`}>
                    <Reveal>
                        <div className="flex items-baseline gap-6">
                            <span className="numeral text-7xl md:text-8xl">{pillar.num}</span>
                            <span className="font-jp text-2xl text-ink-soft">{pillar.jp}</span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="overline mt-4">{pillar.title}</p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-[18ch]">
                            {pillar.heading}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <p className="mt-8 text-ink-soft text-base md:text-lg font-light leading-relaxed max-w-2xl">
                            {pillar.body}
                        </p>
                    </Reveal>
                    <Reveal delay={0.5}>
                        <div className="mt-10 border-t border-ink/15">
                            <p className="overline mt-6 mb-4">How We Help</p>
                            <ul className="space-y-3">
                                {pillar.items.map((it, idx) => (
                                    <motion.li
                                        key={idx}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.6 + idx * 0.08 }}
                                        className="flex items-start gap-4 text-ink font-light text-[15px] md:text-base leading-relaxed"
                                    >
                                        <span className="numeral text-base mt-1">0{idx + 1}</span>
                                        <span>{it}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.7}>
                        <div className="mt-10 p-6 md:p-8 border border-ink/15 bg-aizome/[0.02]">
                            <p className="overline">— Outcome</p>
                            <p className="mt-3 font-serif text-xl md:text-2xl text-ink leading-snug max-w-[40ch]">
                                {pillar.outcome}
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.85}>
                        <Link
                            to="/contact"
                            data-testid={`pillar-cta-${pillar.slug}`}
                            className="btn-ghost mt-10"
                        >
                            Contact Us <span className="arrow">→</span>
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Services;
